import { useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { GlobalState } from "context/GlobalContext";
import { BQ_MASTER_CACHE_KEYS } from "../../shared/bqMasterDropdownCache";
import {
	emptySoItemRow,
	parseItemRate,
	soItemColumnList,
	soMasterFetches,
	syncSoItemDraft,
} from "../data";
import SoColumnField from "../SoColumnField";
import { SoPanel, soColors, soGhostBtnSx, soPrimaryBtnSx, soSecondaryBtnSx } from "../soUi";

const HEADER_ALIASES = {
	item_code: ["item_code", "item code", "item", "icode", "barcode", "sku"],
	qty: ["qty", "quantity", "ordqty", "qnty"],
	rate: ["rate", "price", "mrp", "rsp"],
};

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[_\-]+/g, " ")
		.replace(/\s+/g, " ");

const cellText = (value) => {
	if (value == null) return "";
	if (typeof value === "object" && value.text != null) return String(value.text).trim();
	if (typeof value === "object" && value.result != null) return String(value.result).trim();
	return String(value).trim();
};

const ensureRowId = (row, index) => ({
	...emptySoItemRow(),
	...row,
	_rowId: row?._rowId || row?.id || `so-item-${index}-${row?.item_code || "x"}-${Date.now() % 1e6}`,
});

const itemMatchesCode = (row, code) => {
	const c = String(code);
	return (
		String(row?.key ?? "") === c ||
		String(row?.code ?? "") === c ||
		String(row?.item_code ?? "") === c ||
		String(row?.barcode ?? "") === c
	);
};

/** Prefer GlobalContext items cache; else one masters/items lookup. */
const resolveItemRate = async (itemCode, getDropdownCache) => {
	const code = String(itemCode || "").trim();
	if (!code) return null;

	const cached = getDropdownCache?.(BQ_MASTER_CACHE_KEYS.items) || [];
	const fromCache = cached.find((row) => itemMatchesCode(row, code));
	const cachedRate = parseItemRate(fromCache?.rate);
	if (cachedRate != null) return cachedRate;

	try {
		const res = await soMasterFetches.item_code(0, 20, code);
		const rows = res?.data?.result || [];
		const exact = rows.find((row) => itemMatchesCode(row, code)) || rows[0];
		return parseItemRate(exact?.rate);
	} catch {
		return null;
	}
};

const resolveMissingRates = async (rows, getDropdownCache) => {
	const needCodes = [
		...new Set(
			rows
				.filter((row) => parseItemRate(row.rate) == null && String(row.item_code || "").trim())
				.map((row) => String(row.item_code).trim())
		),
	];
	if (!needCodes.length) return rows;

	const rateByCode = {};
	await Promise.all(
		needCodes.map(async (code) => {
			rateByCode[code] = await resolveItemRate(code, getDropdownCache);
		})
	);

	return rows.map((row) => {
		if (parseItemRate(row.rate) != null) return row;
		const code = String(row.item_code || "").trim();
		const rate = rateByCode[code];
		return rate != null ? { ...row, rate } : row;
	});
};

const mapExcelRows = (headers, rows) => {
	const normalized = headers.map(normalizeHeader);
	const indexOf = (field) => normalized.findIndex((h) => HEADER_ALIASES[field].includes(h));
	const itemIdx = indexOf("item_code");
	const qtyIdx = indexOf("qty");
	const rateIdx = indexOf("rate");

	if (itemIdx < 0) {
		throw new Error("Excel must include an Item / Barcode column (item_code / ICODE / barcode).");
	}

	return rows
		.map((cells, index) => {
			const item_code = cellText(cells?.[itemIdx]);
			if (!item_code) return null;
			const qtyRaw = cellText(cells?.[qtyIdx]);
			const qty = qtyRaw === "" ? 1 : Number(qtyRaw);
			const rateRaw = rateIdx >= 0 ? cellText(cells?.[rateIdx]) : "";
			return ensureRowId(
				{
					item_code,
					qty: Number.isFinite(qty) ? qty : 1,
					rate: rateRaw === "" ? null : parseItemRate(rateRaw),
				},
				index
			);
		})
		.filter(Boolean);
};

const draftFieldWidth = (key) => {
	if (key === "item_code") return { xs: "100%", sm: 260, md: 300 };
	if (key === "qty" || key === "rate") return 96;
	return 120;
};

const SoLineItemsSection = ({ items = [], onChange, readOnly = false }) => {
	const { snackBar, setSnackBar, setSnackBarOpen, getDropdownCache } = GlobalState();
	const fileInputRef = useRef(null);
	const [draftItem, setDraftItem] = useState(emptySoItemRow());
	const [uploading, setUploading] = useState(false);
	const [adding, setAdding] = useState(false);

	const rows = useMemo(() => (items || []).map((row, i) => ensureRowId(row, i)), [items]);

	const setDraftItemSynced = (updater) => {
		setDraftItem((prev) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			return syncSoItemDraft(next);
		});
	};

	const notify = (message, severity = "success") => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message,
			severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const commitRows = (next) => {
		if (typeof onChange === "function") onChange(next);
	};

	const handleAddItem = async () => {
		const synced = syncSoItemDraft(draftItem);
		const item_code = String(synced.item_code || "").trim();
		if (!item_code) {
			notify("Item / barcode is required", "warning");
			return;
		}
		const qty = Number(synced.qty);
		setAdding(true);
		try {
			let rate = parseItemRate(synced.rate);
			if (rate == null) {
				rate = await resolveItemRate(item_code, getDropdownCache);
			}
			commitRows([
				...rows,
				ensureRowId(
					{
						item_code,
						qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
						rate,
					},
					rows.length
				),
			]);
			setDraftItem(emptySoItemRow());
		} finally {
			setAdding(false);
		}
	};

	const handleRemove = (rowId) => {
		commitRows(rows.filter((row) => row._rowId !== rowId));
	};

	const handleDownloadTemplate = async () => {
		const workbook = new ExcelJS.Workbook();
		const sheet = workbook.addWorksheet("SO Items");
		sheet.addRow(["item_code", "qty", "rate"]);
		sheet.addRow(["RR189289", 1, ""]);
		const buffer = await workbook.xlsx.writeBuffer();
		FileSaver.saveAs(
			new Blob([buffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			}),
			"so_line_items_template.xlsx"
		);
	};

	const handleUploadFile = async (file) => {
		if (!file) return;
		setUploading(true);
		try {
			const workbook = new ExcelJS.Workbook();
			await workbook.xlsx.load(await file.arrayBuffer());
			const worksheet = workbook.worksheets[0];
			if (!worksheet) throw new Error("No worksheet found in file");

			const headers = worksheet
				.getRow(1)
				.values.slice(1)
				.map((value) => cellText(value));
			const excelRows = [];
			worksheet.eachRow((row, rowNumber) => {
				if (rowNumber === 1) return;
				excelRows.push(row.values.slice(1));
			});

			let mapped = mapExcelRows(headers, excelRows);
			if (!mapped.length) throw new Error("No valid item rows found in file");

			mapped = await resolveMissingRates(mapped, getDropdownCache);

			const existingCodes = new Set(rows.map((r) => String(r.item_code)));
			const merged = [...rows];
			let added = 0;
			mapped.forEach((row) => {
				if (existingCodes.has(String(row.item_code))) return;
				existingCodes.add(String(row.item_code));
				merged.push(row);
				added += 1;
			});

			commitRows(merged);
			notify(`Uploaded ${added} new line${added === 1 ? "" : "s"} (${mapped.length} in file)`);
		} catch (error) {
			notify(error.message || "Failed to upload items", "error");
		} finally {
			setUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	};

	const tableColumns = useMemo(
		() => [
			{
				key: "item_code",
				name: "Item / Barcode",
				minWidth: 160,
				align: "center",
				headerAlign: "center",
			},
			{
				key: "qty",
				name: "Qty",
				minWidth: 90,
				align: "center",
				headerAlign: "center",
			},
			{
				key: "rate",
				name: "Rate",
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				valueGetter: ({ row }) => (row.rate == null || row.rate === "" ? "—" : row.rate),
			},
			...(!readOnly
				? [
						{
							key: "actions",
							name: "Actions",
							minWidth: 90,
							align: "center",
							headerAlign: "center",
							renderCell: ({ row }) => (
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										handleRemove(row._rowId);
									}}
									sx={{
										color: soColors.muted,
										"&:hover": { color: soColors.danger, bgcolor: soColors.dangerBg },
									}}
									aria-label="Remove item">
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							),
						},
				  ]
				: []),
		],
		[readOnly, rows]
	);

	const tableHeight = Math.max(320, Math.min(560, 72 + Math.max(rows.length, 4) * 48));

	return (
		<SoPanel
			title="Line items"
			subtitle={rows.length ? `${rows.length} line${rows.length === 1 ? "" : "s"}` : "Scan / type item codes"}
			actions={
				!readOnly ? (
					<Stack direction="row" spacing={0.75}>
						<Button
							size="small"
							startIcon={<DownloadRoundedIcon />}
							onClick={handleDownloadTemplate}
							sx={soGhostBtnSx}>
							Template
						</Button>
						<Button
							size="small"
							startIcon={<UploadFileIcon />}
							onClick={() => fileInputRef.current?.click()}
							disabled={uploading}
							sx={soSecondaryBtnSx}
							variant="outlined">
							{uploading ? "Uploading…" : "Upload"}
						</Button>
						<input
							ref={fileInputRef}
							type="file"
							accept=".xlsx,.xls"
							hidden
							onChange={(e) => handleUploadFile(e.target.files?.[0])}
						/>
					</Stack>
				) : null
			}
			contentSx={{ px: 0, py: 0 }}>
			{!readOnly && (
				<Box
					sx={{
						px: 1.75,
						py: 1.1,
						borderBottom: `1px solid ${soColors.line}`,
						display: "flex",
						flexWrap: "wrap",
						alignItems: "flex-end",
						gap: 1.25,
					}}>
					{soItemColumnList.map((column) => (
						<SoColumnField
							key={column.key}
							column={column}
							data={draftItem}
							setData={setDraftItemSynced}
							dense
							readOnly={!!column.disabled}
							width={draftFieldWidth(column.key)}
						/>
					))}
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleAddItem}
						disabled={adding}
						sx={{ ...soPrimaryBtnSx, height: 36, alignSelf: "flex-end" }}
						size="small">
						{adding ? "Adding…" : "Add"}
					</Button>
				</Box>
			)}

			<Box sx={{ height: tableHeight, p: 0.75 }}>
				<TanstackDatagrid
					columns={tableColumns}
					pageData={rows}
					setPageData={() => {}}
					rowCount={rows.length}
					setRowCount={() => {}}
					serverSidePagination={false}
					disableColumnFilter
					disableColumnSort
					disablePagination
					isLoading={false}
					emptyMessage="No line items yet"
					getRowId={(row) => row._rowId}
					rowHeight={44}
				/>
			</Box>
		</SoPanel>
	);
};

export default SoLineItemsSection;
